import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import _ from 'lodash';
import QRCode from 'qrcode.react';
import WrapperContainer from '../../components/Container/WrapperContainer';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Typography from '../../components/Typography/CustomTypography';
import Button from '../../components/Button/CustomButton';
import styles from '../../assets/jss/pages/homePageStyle';

import dateUtils from '../../utils/dateUtils';
import Loader from '../../components/Loader/Loader';
import Advertisement from '../../components/Advertisement/Advertisement';
import { Role } from '../../common/Constants';
import SnackBar from '../../components/Snackbar/Snackbar';
import { RadioButtonIcon } from '../../components/Icons/Icon';
import SuccessDialog from '../../components/Dialog/SuccessDialog';
import FreeLogin from '../../components/FreeEntrance/FreeLogin';
import SeminarLogin from '../../components/FreeEntrance/SeminarLogin';

const useStyles = makeStyles(styles);

const HomePage = (props) => {
    const {
        eventInfo,
        role,
        isAuthorized,
        isLoading,
        loginOuterServer,
        errorLoginOuterServer,
        dataLoginOuterServer,
        clearLoginOuterServer,
        freeLogin,
        customColor,
        getCustomText,
        customText,
        clearCustomText,
        getSettingCustomComment,
        customComment,
        clearGetSettingCustomComment,
        locale,
        changeLanguage,
    } = props;

    const classes = useStyles(props);
    const history = useHistory();
    const intl = useIntl();
    const routeParams = useParams();
    const { search } = useLocation();
    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const [isOpenSnackBar, setIsOpenSnackBar] = useState({ isOpen: false, message: '' });

    useEffect(() => {
        const { shortName } = routeParams;
        getCustomText({
            eventShortName: shortName,
            site: 0,
            text_type: 5,
            language: locale,
        });
        getSettingCustomComment({ eventShortName: shortName, language: locale });
        if (!_.isEmpty(search)) {
            const params = new URLSearchParams(search);
            if (!_.isEmpty(params.get('token'))) {
                loginOuterServer(params.get('token'));
            }
            if (!_.isEmpty(params.get('free_login')) && params.get('free_login') === 'true') {
                freeLogin({ shortName });
            }
        }
        return () => {
            clearLoginOuterServer();
            clearCustomText();
            clearGetSettingCustomComment();
        };
    }, [locale]);

    useEffect(() => {
        if (isAuthorized && role !== Role.admin && !_.isEmpty(dataLoginOuterServer)) {
            if (role === Role.viewer) {
                history.push(`/${routeParams.shortName}/home`);
            } else if (role === Role.organizer) {
                changeLanguage('ja');
                history.push(`/${routeParams.shortName}/organizer/home`);
            }
        }
    }, [isAuthorized, role, dataLoginOuterServer, locale]);

    useEffect(() => {
        if (!_.isEmpty(errorLoginOuterServer)) {
            setIsOpenSnackBar({ isOpen: true, message: errorLoginOuterServer?.message?.message || errorLoginOuterServer?.message });
        }
    }, [errorLoginOuterServer]);

    const handleLinkToRegisterOrganizer = () => {
        const utcDateNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
        utcDateNow.setHours(0, 0, 0, 0);
        const timeRunningStart = new Date(eventInfo.sdate);
        const time7Day = new Date(timeRunningStart.setDate(timeRunningStart.getDate() - 7));
        time7Day.setHours(0, 0, 0, 0);
        if (time7Day < utcDateNow) {
            setIsOpenDialog(true);
        } else {
            history.push(`/${eventInfo.short_name}/register-exhibitor`);
        }
    };

    const handleLinkToRegisterViewer = () => {
        history.push(`/${eventInfo.short_name}/register-visitor`);
    };
    const handleLoginVisitor = () => {
        history.push(`/${eventInfo.short_name}/visitor_login`);
    };

    const handleLoginExhibitor = () => {
        history.push(`/${eventInfo.short_name}/exhibitor_login`);
    };

    // helpers
    const setWelcomeText = () => {
        const textTypes = {
            free: intl.formatMessage({ id: 'home.noteThanksViewer' }),
            viewer: intl.formatMessage({ id: 'home.noteThanksViewer' }),
            organizer: intl.formatMessage({ id: 'home.noteThanksOrganizer' }),
        };
        let textString;
        let isDisplay = false;

        if (isAuthorized && Number(role) === Role.viewer) {
            isDisplay = true;
            textString = textTypes.viewer;
        }

        if (isAuthorized && Number(role) === Role.organizer) {
            isDisplay = true;
            textString = textTypes.organizer;
        }

        return isDisplay
            ? (
                <Typography className={classes.blackText} bold variant="h4" align="center" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                    {textString}
                </Typography>
            )
            : null;
    };

    return (
        <>
            <WrapperContainer>
                <GridItem xs={12} md={12} className={classes.body}>
                    <div className={Number(role) === Role.organizer ? classes.contentDivOrganizer : classes.contentDiv}>
                        <div className={classes.background}>
                            <GridContainer>
                                <GridItem xs={12} md={5} style={{ height: '0' }} />
                                <GridItem xs={12} md={7} className={classes.wrapperRight}>
                                    <Box className={classes.content}>
                                        <Box paddingBottom={2} paddingTop={2}>
                                            <GridContainer>
                                                <GridItem xs={12} sm={12} md={12}>
                                                    <Typography bold variant="h3" color="info" align="center" customColor={Number(role) === Role.organizer ? '' : customColor?.event_name_text_color}>
                                                        {eventInfo.name}
                                                    </Typography>
                                                    {setWelcomeText()}
                                                </GridItem>
                                            </GridContainer>
                                        </Box>
                                        <Divider className={classes.divider} />
                                        <GridContainer className={classes.right}>
                                            <GridItem xs={12} md={2} />
                                            <GridItem xs={12} md={3}>
                                                <Typography bold variant="h6" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                                                    {customText?.field_name?.holding_period || intl.formatMessage({ id: 'event.heldDate' })}
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <Typography color="info" bold variant="h6" customColor={Number(role) === Role.organizer ? '' : customColor?.event_name_text_color}>
                                                    {(eventInfo.sdate && eventInfo.edate) && dateUtils.formatRange(eventInfo.sdate, eventInfo.edate, locale)}
                                                </Typography>
                                            </GridItem>
                                        </GridContainer>
                                        <GridContainer className={classes.right}>
                                            <GridItem xs={12} md={2} />
                                            <GridItem xs={12} md={3}>
                                                <Typography bold variant="h6" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                                                    {customText?.field_name?.open_time || intl.formatMessage({ id: 'event.heldTime' })}
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={6} md={3}>
                                                <Typography color="info" bold variant="h6" customColor={Number(role) === Role.organizer ? '' : customColor?.event_name_text_color}>
                                                    {`${eventInfo.open_time} ~ ${eventInfo.close_time}`}
                                                </Typography>
                                                {eventInfo.note && (
                                                    <Typography variant="subtitle1" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                                                        {eventInfo.note}
                                                    </Typography>
                                                )}
                                            </GridItem>
                                            {!isAuthorized && (
                                                <GridItem xs={6} md={3}>
                                                    <QRCode
                                                        value={window.location.href}
                                                        className={classes.QACode}
                                                    />
                                                </GridItem>
                                            )}
                                        </GridContainer>
                                        <GridContainer className={classes.right}>
                                            <GridItem xs={12} md={2} />
                                            <GridItem xs={12} md={3}>
                                                <Typography bold variant="h6" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                                                    {customText?.field_name?.venue || intl.formatMessage({ id: 'event.heldPlace' })}
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <Typography style={{ fontSize: 20 }} variant="subtitle1" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                                                    {eventInfo.address}
                                                </Typography>
                                            </GridItem>
                                        </GridContainer>
                                        <GridContainer className={classes.right}>
                                            <GridItem xs={12} md={2} />
                                            <GridItem xs={12} md={3}>
                                                <Typography bold variant="h6" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                                                    {customText?.field_name?.overview || intl.formatMessage({ id: 'home.overview' })}
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <Typography style={{ fontSize: 20 }} variant="subtitle1" customColor={Number(role) === Role.organizer ? '' : customColor?.contents_text_color}>
                                                    {eventInfo.description}
                                                </Typography>
                                            </GridItem>
                                        </GridContainer>
                                    </Box>
                                </GridItem>
                            </GridContainer>
                        </div>

                        {!isAuthorized && (
                            <GridContainer justify="center" style={{ margin: '60px auto 30px auto' }}>
                                {!(eventInfo.has_live_stream && !eventInfo.free_live_stream)
                                && !(eventInfo.has_seminar && !eventInfo.free_seminar)
                                && !(eventInfo.has_exhibition && !eventInfo.free_exhibition) && (
                                    <FreeLogin customColor={customColor} />
                                )}
                                {((eventInfo.has_live_stream && !eventInfo.free_live_stream)
                                || (eventInfo.has_seminar && !eventInfo.free_seminar)
                                || (eventInfo.has_exhibition && !eventInfo.free_exhibition))
                                && !(!eventInfo.free_exhibition && !eventInfo.free_seminar && !eventInfo.free_live_stream)
                                && (
                                    <>
                                        <FreeLogin customColor={customColor} />
                                        <SeminarLogin eventInfo={eventInfo} customColor={customColor} />
                                    </>
                                )}
                                {!eventInfo.free_exhibition && !eventInfo.free_seminar && !eventInfo.free_live_stream && (
                                    <GridItem container item className={classes.sectionWrapper} justify={!eventInfo.outer_server_activation ? 'center' : 'space-around'}>
                                        {!eventInfo.outer_server_activation && (
                                            <GridItem xs={12} lg={6} className={classes.sectionLeft}>
                                                <div className={classes.centerToRight}>
                                                    <Button
                                                        color="info"
                                                        gradient
                                                        className={classes.buttonBig}
                                                        round
                                                        onClick={handleLinkToRegisterViewer}
                                                        buttonTextColor={customColor?.button_text_color}
                                                        buttonBackgroundColor={customColor?.button_background_color?.viewer}
                                                    >
                                                        {intl.formatMessage({ id: 'home.admissionApplication' })}
                                                    </Button>
                                                </div>
                                            </GridItem>
                                        )}

                                        <GridItem xs={12} lg={6}>
                                            <div className={classes.centerToLeft}>
                                                <Button
                                                    color="info"
                                                    gradient
                                                    className={classes.buttonBig}
                                                    round
                                                    onClick={handleLoginVisitor}
                                                    buttonTextColor={customColor?.button_text_color}
                                                    buttonBackgroundColor={customColor?.button_background_color?.viewer}
                                                >
                                                    {intl.formatMessage({ id: 'home.loginViewer' })}
                                                </Button>
                                            </div>
                                            <Box display="flex">
                                                <RadioButtonIcon color={customColor?.button_background_color?.viewer || '#4facfe'} />
                                                <Typography className={classes.rightContent} variant="subtitle1" customColor={customColor?.contents_text_color}>
                                                    {intl.formatMessage({ id: 'home.textLogin' }, {
                                                        textExhibit: eventInfo.settings_texts_sidebar_viewer?.field_name?.exhibitor_info_list
                                                        || intl.formatMessage({ id: 'exhibited.products' }),
                                                        textSeminar: eventInfo.settings_texts_sidebar_viewer?.field_name?.seminar_list
                                                        || intl.formatMessage({ id: 'login.seminars' }).toLocaleLowerCase(),
                                                        textLivestream: eventInfo.settings_texts_sidebar_viewer?.field_name?.livestreaming
                                                        || intl.formatMessage({ id: 'userRouters.liveStream' }).toLocaleLowerCase(),
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Box display="flex">
                                                <RadioButtonIcon color={customColor?.button_background_color?.viewer || '#4facfe'} />
                                                <Typography className={classes.rightContent} variant="subtitle1" customColor={customColor?.contents_text_color}>
                                                    {intl.formatMessage({ id: 'home.textRegisterViewer' })}
                                                </Typography>
                                            </Box>
                                        </GridItem>
                                    </GridItem>
                                )}

                                <GridItem container item className={classes.sectionWrapper} justify={!eventInfo.outer_server_activation ? 'center' : 'space-around'}>
                                    {!eventInfo.outer_server_activation && (
                                        <GridItem xs={12} lg={6} className={classes.sectionLeft}>
                                            <div className={classes.centerToRight}>
                                                <Button
                                                    color="warning"
                                                    gradient
                                                    className={classes.buttonBig}
                                                    round
                                                    onClick={handleLinkToRegisterOrganizer}
                                                    buttonTextColor={customColor?.button_text_color}
                                                    buttonBackgroundColor={customColor?.button_background_color?.organizer}
                                                >
                                                    {intl.formatMessage({ id: 'home.exhibitApplication' })}
                                                </Button>
                                            </div>
                                        </GridItem>
                                    )}

                                    <GridItem xs={12} lg={6}>
                                        <div className={classes.centerToLeft}>
                                            <Button
                                                color="warning"
                                                gradient
                                                className={classes.buttonBig}
                                                round
                                                onClick={handleLoginExhibitor}
                                                buttonTextColor={customColor?.button_text_color}
                                                buttonBackgroundColor={customColor?.button_background_color?.organizer}
                                            >
                                                {intl.formatMessage({ id: 'home.loginExhibit' })}
                                            </Button>
                                        </div>
                                        <Box display="flex">
                                            <RadioButtonIcon color={customColor?.button_background_color?.organizer || '#f57c00'} />
                                            <Typography className={classes.rightContent} variant="subtitle1" customColor={customColor?.contents_text_color}>
                                                {intl.formatMessage({ id: 'home.textLoginExhibit' })}
                                            </Typography>
                                        </Box>
                                        <Box display="flex">
                                            <RadioButtonIcon color={customColor?.button_background_color?.organizer || '#f57c00'} />
                                            <Typography className={classes.rightContent} variant="subtitle1" customColor={customColor?.contents_text_color}>
                                                {intl.formatMessage({ id: 'home.textRegisterOrganizer' })}
                                            </Typography>
                                        </Box>
                                    </GridItem>
                                </GridItem>

                                {eventInfo.organization_request_url && (
                                    <GridItem container item className={classes.sectionWrapper} justify={!eventInfo.outer_server_activation ? 'center' : 'space-around'}>
                                        {!eventInfo.outer_server_activation && (
                                            <GridItem xs={12} lg={6} />
                                        )}

                                        <GridItem xs={12} lg={6}>
                                            <div className={classes.centerToLeft}>
                                                <Button
                                                    color="danger"
                                                    gradient
                                                    className={classes.buttonBig}
                                                    round
                                                    href={eventInfo.organization_request_url}
                                                    target="_blank"
                                                    buttonTextColor={customColor?.button_text_color}
                                                    buttonBackgroundColor={customColor?.button_background_color?.instruction}
                                                >
                                                    {customText.button_name?.exhibitor_guidelines || intl.formatMessage({ id: 'home.guidelinesExhibitor' })}
                                                </Button>
                                            </div>
                                        </GridItem>
                                    </GridItem>
                                )}
                            </GridContainer>
                        )}
                    </div>
                    {!_.isEmpty(customComment?.content_exhibitor) && (Number(role) === Role.organizer) && (
                        <div className={classes.commentWrapper}>
                            <GridContainer>
                                <div dangerouslySetInnerHTML={{ __html: customComment.content_exhibitor }} />
                            </GridContainer>
                        </div>
                    )}
                    {!_.isEmpty(customComment?.content_visitor) && !(Number(role) === Role.organizer) && (
                        <div className={classes.commentWrapper}>
                            <GridContainer>
                                <div dangerouslySetInnerHTML={{ __html: customComment.content_visitor }} />
                            </GridContainer>
                        </div>
                    )}
                    {eventInfo.display_ads && (
                        <GridContainer justify="center">
                            <Advertisement customColor={customColor} />
                        </GridContainer>
                    )}
                </GridItem>
            </WrapperContainer>
            <SuccessDialog
                title=""
                open={isOpenDialog}
                contents={(
                    <Typography customColor={customColor?.contents_text_color}>
                        {intl.formatMessage({ id: 'home.textRegisterFail' })}
                        <br />
                        {intl.formatMessage({ id: 'home.textRegisterFailNote' })}
                    </Typography>
                )}
                customColor={customColor}
                handleClose={() => setIsOpenDialog(false)}
            />
            <Loader isLoading={isLoading} />
            <SnackBar
                open={isOpenSnackBar.isOpen}
                onClose={() => setIsOpenSnackBar({ isOpen: false, message: '' })}
                message={isOpenSnackBar.message}
                severity="error"
                autoHideDuration={3000}
            />
        </>
    );
};

HomePage.defaultProps = {
    eventInfo: {},
    isAuthorized: false,
    role: 0,
    loginOuterServer: () => {},
    errorLoginOuterServer: {},
    dataLoginOuterServer: {},
    clearLoginOuterServer: () => {},
    freeLogin: () => {},
    customColor: {},
    getCustomText: () => {},
    customText: {},
    clearCustomText: () => {},
    getSettingCustomComment: () => {},
    customComment: {},
    clearGetSettingCustomComment: () => {},
    locale: '',
    changeLanguage: () => {},
};

HomePage.propTypes = {
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    isLoading: PropTypes.bool.isRequired,
    isAuthorized: PropTypes.bool,
    role: PropTypes.number,
    loginOuterServer: PropTypes.func,
    errorLoginOuterServer: PropTypes.oneOfType([() => null, PropTypes.object]),
    dataLoginOuterServer: PropTypes.oneOfType([() => null, PropTypes.object]),
    clearLoginOuterServer: PropTypes.func,
    freeLogin: PropTypes.func,
    customColor: PropTypes.oneOfType([() => null, PropTypes.object]),
    getCustomText: PropTypes.func,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    clearCustomText: PropTypes.func,
    getSettingCustomComment: PropTypes.func,
    customComment: PropTypes.oneOfType([() => null, PropTypes.object]),
    clearGetSettingCustomComment: PropTypes.func,
    locale: PropTypes.string,
    changeLanguage: PropTypes.func,
};

export default HomePage;
